// useHistory.js — histórico genérico com undo/redo baseado em snapshots
// Uso: passe getSnapshot() e applySnapshot(snap), e chame push() após mudanças.

import { ref } from 'vue'

export function useHistory(getSnapshot, applySnapshot, opts = {}) {
  const limit = opts.limit ?? 100
  const stack = ref([])   // [{ snap: string, label: string, ts: number }]
  const index = ref(-1)

  function _apply() {
    const entry = stack.value[index.value]
    if (!entry) return
    const data = JSON.parse(entry.snap)
    applySnapshot(data)
  }

  function push(label = '') {
    const snapObj = getSnapshot()
    const snap = JSON.stringify(snapObj)

    // Se usuário fez undo e depois mexeu, descartamos o "futuro"
    if (index.value < stack.value.length - 1) {
      stack.value.splice(index.value + 1)
    }

    stack.value.push({ snap, label, ts: Date.now() })
    if (stack.value.length > limit) {
      stack.value.shift()
      index.value -= 1
    }
    index.value = stack.value.length - 1
  }

  // Substitui a última entrada (útil para drag move / pointermove debounce)
  function replaceLast(label = '') {
    if (index.value < 0) return push(label)
    const snapObj = getSnapshot()
    const snap = JSON.stringify(snapObj)
    stack.value[index.value] = { snap, label, ts: Date.now() }
  }

  function undo() {
    if (index.value <= 0) return false
    index.value -= 1
    _apply()
    return true
  }

  function redo() {
    if (index.value >= stack.value.length - 1) return false
    index.value += 1
    _apply()
    return true
  }

  function canUndo() { return index.value > 0 }
  function canRedo() { return index.value < stack.value.length - 1 }
  function clear() { stack.value = []; index.value = -1 }

  return { push, replaceLast, undo, redo, canUndo, canRedo, clear, index, stack }
}

// Atalhos de teclado: Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z ou Ctrl/Cmd+Y (redo)
export function registerHistoryHotkeys(target, history) {
  const handler = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return
    const k = e.key?.toLowerCase?.()
    if (k === 'z') { e.preventDefault(); e.shiftKey ? history.redo() : history.undo() }
    if (k === 'y') { e.preventDefault(); history.redo() }
  }
  target.addEventListener('keydown', handler)
  return () => target.removeEventListener('keydown', handler)
}
